<template>
  <BottomSheet
    v-if="activity && sheet"
    v-model="snap"
    class="activity-sheet"
    :class="{ 'activity-sheet--expanded': snap === 'expanded' }"
    :snap-points="SNAP_POINTS"
  >
    <template #header>
      <div class="as-header">
        <ActivityPanelTabs compact :tabs="tabs" :selected="selected" @select="selectFromSheet" />
        <button
          type="button"
          class="ap-icon-button as-expand"
          :title="snap === 'expanded' ? $t('hike_map.elevation_profile.minimize') : $t('hike_map.elevation_profile.maximize')"
          @click.stop="toggleSheetExpanded"
        >
          <span class="material-icons">{{ snap === 'expanded' ? 'close_fullscreen' : 'open_in_full' }}</span>
        </button>
      </div>
    </template>

    <div class="as-body">
      <ActivityPanelStats class="as-stats" :stats="stats" :color="TAB_STYLES[selected].color" />
      <ActivityPanelFigures v-if="snap === 'expanded'" class="as-figures" :figures="figures" />
      <div class="ap-chart">
        <ElevationProfile
          v-if="selected === 'elevation'"
          :coordinates="coordinates"
          :get-map="getMap"
          :max-points="120"
          :collapsible="false"
          :expandable="false"
          :show-stats="false"
        />
        <ActivityChart
          v-else-if="series"
          :series="series"
          :x-unit="pref.kilometerSymbol()"
          :backdrop-minimum-spread="toSmallUnit(MINIMUM_ELEVATION_SPREAD_METERS, pref)"
          :tooltip="tooltip"
          :high-point="highPoint"
          @hover="showMarker"
          @hover-end="hideMarker"
        />
      </div>
      <ActivityPanelFooter
        v-if="snap === 'expanded' && hasFooter"
        stacked
        :legend="legend"
        :ascent-rows="ascentRows"
        :insight="insight"
        :cadence="cadence"
      />
    </div>
  </BottomSheet>

  <!-- The info card floats above the panel, which clips its own overflow. -->
  <div v-else-if="activity" class="activity-panel-wrap">
    <ActivityChartInfo v-if="isShowingInfo && !collapsed" class="ap-info-card" :tab="selected" @close="isShowingInfo = false" />
    <div
      class="activity-panel"
      :class="{ 'activity-panel--collapsed': collapsed, 'activity-panel--expanded': expanded && !collapsed }"
    >
      <div class="ap-header">
        <button
          type="button"
          class="ap-icon-button ap-collapse"
          :title="collapsed ? $t('hike_map.elevation_profile.show_elevation_profile') : $t('hike_map.elevation_profile.hide_elevation_profile')"
          @click="collapsed = !collapsed"
        >
          <span class="material-icons">{{ collapsed ? 'expand_less' : 'expand_more' }}</span>
        </button>

        <ActivityPanelTabs :tabs="tabs" :selected="selected" @select="select" />

        <button
          type="button"
          class="ap-icon-button ap-info-button"
          :class="{ 'ap-info-button--open': isShowingInfo && !collapsed }"
          :aria-expanded="isShowingInfo && !collapsed"
          :aria-label="selectedInfoLabel"
          :title="selectedInfoLabel"
          @click="toggleInfo"
        >
          <span class="material-icons" aria-hidden="true">info</span>
        </button>

        <ActivityPanelStats class="ap-header__stats" :stats="stats" :color="TAB_STYLES[selected].color" />

        <button
          type="button"
          class="ap-icon-button ap-expand"
          :title="expanded ? $t('hike_map.elevation_profile.minimize') : $t('hike_map.elevation_profile.maximize')"
          @click="toggleExpanded"
        >
          <span class="material-icons">{{ expanded ? 'fullscreen_exit' : 'fullscreen' }}</span>
        </button>
      </div>

      <template v-if="!collapsed">
        <ActivityPanelFigures v-if="expanded" :figures="figures" />

        <div class="ap-chart">
          <ElevationProfile
            v-if="selected === 'elevation'"
            :coordinates="coordinates"
            :get-map="getMap"
            :max-points="120"
            :collapsible="false"
            :expandable="false"
            :show-stats="false"
          />
          <ActivityChart
            v-else-if="series"
            :series="series"
            :x-unit="pref.kilometerSymbol()"
            :backdrop-minimum-spread="toSmallUnit(MINIMUM_ELEVATION_SPREAD_METERS, pref)"
            :tooltip="tooltip"
            :high-point="highPoint"
            @hover="showMarker"
            @hover-end="hideMarker"
          />
        </div>

        <ActivityPanelFooter
          v-if="expanded && hasFooter"
          :legend="legend"
          :ascent-rows="ascentRows"
          :insight="insight"
          :cadence="cadence"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type mapboxgl from 'mapbox-gl';
import BottomSheet, { type SnapPoint } from '../../components/BottomSheet.vue';
import ElevationProfile from '../../components/ElevationProfile.vue';
import ActivityChart from '../../recording_activity/components/ActivityChart.vue';
import { createElevationMarker } from '../../../utils/elevation/elevationMarker';
import { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref';
import { activityStats, cadenceInsight, scaleLegend } from '../../../utils/recordingActivity/activityPanel';
import { MINIMUM_ELEVATION_SPREAD_METERS } from '../../../utils/recordingActivity/colorScale';
import { toSmallUnit } from '../../../utils/recordingActivity/format';
import {
  activityCard,
  activityFigures,
  activitySeries,
  activityTabs,
  activityTooltip,
  ascentInsight,
  ascentLegend,
  backdropHighPoint,
  recordingActivity,
  type ActivityTab,
  type ChartPoint,
  type RecordingActivityInput,
} from '../../../utils/recordingActivity/recordingActivity';
import { ACTIVITY_TAB_STYLES as TAB_STYLES } from '../../../utils/recordingActivity/tabStyles';
import { trackPositioner } from '../helpers/trackPosition';
import ActivityChartInfo from './ActivityChartInfo.vue';
import ActivityPanelFigures from './ActivityPanelFigures.vue';
import ActivityPanelFooter from './ActivityPanelFooter.vue';
import ActivityPanelStats from './ActivityPanelStats.vue';
import ActivityPanelTabs from './ActivityPanelTabs.vue';

type SheetSnap = 'peek' | 'default' | 'expanded';

// Peek matches the 88px the recording card's Details bar is lifted by (TrailDataCard).
const SNAP_POINTS: SnapPoint[] = [
  { key: 'peek', heightPx: 88 },
  { key: 'default', heightPx: 300 },
  { key: 'expanded', heightPx: '75vh' },
];

const props = defineProps<{
  input: RecordingActivityInput;
  /** The track as drawn on the map, `[lon, lat, (ele)]`. */
  coordinates: number[][];
  getMap: () => mapboxgl.Map | null | undefined;
  /** A phone: a bottom sheet docked along the map's foot instead of the floating panel. */
  sheet?: boolean;
}>();

const emit = defineEmits<{ (e: 'snapChange', snap: SheetSnap): void }>();

const { t, locale } = useI18n();
const pref = MeasurementPrefUtility.preference();

const activity = recordingActivity(props.input);
const tabKeys = activity ? activityTabs(activity) : [];
const selected = ref<ActivityTab>(tabKeys.includes('elevation') ? 'elevation' : tabKeys[0]);
const collapsed = ref(false);
const expanded = ref(false);
const isShowingInfo = ref(false);
const snap = ref<SheetSnap>('peek');

const tabs = computed(() => tabKeys.map((tab) => ({ tab, title: activityCard(tab, activity!, pref, t, locale.value).title })));
const selectedInfoLabel = computed(() =>
  t('recordings.activity.learn_more', { topic: tabs.value.find((tab) => tab.tab === selected.value)?.title ?? '' })
);
const stats = computed(() => activityStats(selected.value, activity!, pref, t, locale.value));
const figures = computed(() => activityFigures(selected.value, activity!, pref, t, locale.value));
const series = computed(() => (selected.value === 'elevation' ? null : activitySeries(selected.value, activity!, pref, t)));
const highPoint = computed(() => backdropHighPoint(series.value, pref, t, locale.value));
const legend = computed(() => scaleLegend(selected.value, series.value, t));
const ascentRows = computed(() => (selected.value === 'ascent' && series.value ? ascentLegend(series.value, pref, t, locale.value) : []));
const insight = computed(() => (selected.value === 'ascent' ? ascentInsight(activity!, pref, locale.value) : null));
const cadence = computed(() => (selected.value === 'steps' ? cadenceInsight(activity!, locale.value) : null));
const hasFooter = computed(() => legend.value.length > 0 || ascentRows.value.length > 0 || !!insight.value || !!cadence.value);
const positionAt = computed(() => trackPositioner(props.coordinates, activity?.totalMeters ?? 0));

const marker = createElevationMarker(() => props.getMap());

watch(snap, (value) => emit('snapChange', value), { immediate: true });

function select(tab: ActivityTab) {
  selected.value = tab;
  marker.hide();
}

// Picking a graph from the peeked sheet means wanting to see it.
function selectFromSheet(tab: ActivityTab) {
  select(tab);
  if (snap.value === 'peek') snap.value = 'default';
}

function toggleExpanded() {
  expanded.value = !expanded.value;
  collapsed.value = false;
}

function toggleInfo() {
  isShowingInfo.value = !(isShowingInfo.value && !collapsed.value);
  collapsed.value = false;
}

function closeInfoOnEscape(event: KeyboardEvent) {
  if (event.key === 'Escape') isShowingInfo.value = false;
}

watch(isShowingInfo, (isShowing) => {
  if (isShowing) document.addEventListener('keydown', closeInfoOnEscape);
  else document.removeEventListener('keydown', closeInfoOnEscape);
});

function toggleSheetExpanded() {
  snap.value = snap.value === 'expanded' ? 'default' : 'expanded';
}

function tooltip(point: ChartPoint) {
  return activityTooltip(point, series.value!, activity!, pref, t);
}

function showMarker(x: number) {
  const lngLat = positionAt.value(pref.metresFromBigUnit(x));
  if (lngLat) marker.show(lngLat);
}

function hideMarker() {
  marker.hide();
}

onBeforeUnmount(() => {
  marker.destroy();
  document.removeEventListener('keydown', closeInfoOnEscape);
});
</script>

<style scoped>
.ap-info-card {
  position: absolute;
  bottom: calc(100% + 12px);
  left: 28px;
  width: min(480px, calc(100% - 28px));
}

.activity-panel {
  display: flex;
  flex-direction: column;
  container-type: inline-size;
  overflow: hidden;
  color: #fff;
  background: rgba(40, 42, 50, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.4);
}

.ap-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  background: rgba(30, 32, 38, 0.8);
}

.activity-panel:not(.activity-panel--collapsed) .ap-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.ap-header .ap-header__stats {
  margin-left: auto;
}

.ap-icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  color: #fff;
  background: none;
  border: none;
  border-radius: 50%;
}

.ap-icon-button:hover {
  background: rgba(255, 255, 255, 0.1);
}

.ap-info-button--open,
.ap-info-button--open:hover {
  color: #17181d;
  background: #fff;
}

.ap-expand {
  margin-left: 4px;
}

.ap-chart {
  height: 150px;
  padding: 8px 12px 0 4px;
}

.activity-panel--expanded .ap-chart {
  height: 280px;
}

.ap-chart :deep(.activity-chart) {
  height: 100%;
}

/* The map's own elevation profile, framed by this panel: its header and card chrome are ours. */
.ap-chart :deep(.elevation-profile) {
  height: 100%;
  background: none;
  box-shadow: none;
  backdrop-filter: none;
  border-radius: 0;
}

.ap-chart :deep(.elevation-profile .profile-header) {
  display: none;
}

/* A narrow map can't fit the stats beside the tabs; the expanded view still lists them. */
@container (max-width: 940px) {
  .ap-header .ap-header__stats {
    display: none;
  }

  .ap-expand {
    margin-left: auto;
  }
}

/* The phone's sheet. */
.as-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}

.as-expand {
  width: 30px;
  height: 30px;
}

.as-expand .material-icons {
  font-size: 18px;
}

.as-body {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.as-body .as-stats {
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 2px 14px;
  padding: 0 14px 6px;
}

.as-stats :deep(.ap-stat) {
  font-size: 0.85rem;
}

.as-body .as-figures {
  flex-shrink: 0;
  padding: 4px 14px;
  gap: 0.5rem 1.75rem;
}

.as-body .ap-chart {
  flex: 1;
  min-height: 150px;
  height: auto;
  padding: 4px 8px 4px 0;
}

.activity-sheet--expanded .as-body .ap-chart {
  min-height: 240px;
}
</style>
